/**
 * SectionsEditor — edits a JSON list of section blocks.
 * Each section: {type, heading?, eyebrow?, paragraphs[], list_items[], cards[], ctas[], links[], theme?, image?, faqs[]}.
 *
 * For simple fields (heading, eyebrow, paragraphs, list_items) we render form inputs.
 * For complex arrays (cards, ctas, faqs) we render a JSON textarea with validate-on-blur so admins can edit them precisely.
 * This keeps the UI compact while remaining fully-featured.
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

const SECTION_TYPES = [
  "hero", "page_banner", "cards_grid", "process_steps", "numbered_list",
  "values_list", "standards_list", "content_aside", "split_content",
  "split_list", "list_items", "faq", "legal_block", "cta", "rich_text",
];

const THEMES = ["white", "off", "navy"];

function TextArea({ label, value, onChange, rows = 3, testid }) {
  return (
    <label className="block mb-3">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">{label}</span>
      <textarea
        data-testid={testid}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm px-3 py-2.5 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold font-mono"
      />
    </label>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block mb-3">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">{label}</span>
      <input
        type={type}
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 text-sm px-3 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
      />
    </label>
  );
}

function StringList({ label, values = [], onChange, testid }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">{label}</div>
      {values.map((v, i) => (
        <div key={i} className="flex gap-2 mb-1.5">
          <textarea
            data-testid={`${testid}-${i}`}
            rows={2}
            value={v}
            onChange={(e) => {
              const next = [...values];
              next[i] = e.target.value;
              onChange(next);
            }}
            className="flex-1 text-sm px-3 py-2 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
          />
          <button
            type="button"
            onClick={() => onChange(values.filter((_, j) => j !== i))}
            className="h-8 w-8 self-start text-mist-navy/60 hover:text-red-600"
            aria-label="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...values, ""])}
        data-testid={`${testid}-add`}
        className="inline-flex items-center gap-1.5 text-xs text-mist-navy hover:text-mist-gold"
      >
        <Plus className="h-3.5 w-3.5" /> Add
      </button>
    </div>
  );
}

function JsonField({ label, value, onChange, testid, rows = 6 }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? [], null, 2));
  const [err, setErr] = useState(null);
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">{label} (JSON)</div>
      <textarea
        data-testid={testid}
        rows={rows}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            setErr(null);
            onChange(parsed);
          } catch (ex) {
            setErr(ex.message);
          }
        }}
        className={`w-full text-xs font-mono px-3 py-2 bg-mist-off border rounded-md focus:outline-none focus:border-mist-gold ${
          err ? "border-red-400" : "border-mist-soft"
        }`}
      />
      {err && <div className="mt-1 text-xs text-red-600" role="alert">{err}</div>}
    </div>
  );
}

function SectionEditor({ section, onChange, onRemove, onMoveUp, onMoveDown, index }) {
  const set = (patch) => onChange({ ...section, ...patch });
  return (
    <div className="bg-white border border-mist-soft rounded-md p-5 mb-4" data-testid={`section-${index}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[11px] uppercase tracking-[0.22em] text-mist-navy/50 font-semibold mr-2">Section {index + 1}</span>
        <select
          data-testid={`section-${index}-type`}
          value={section.type || "rich_text"}
          onChange={(e) => set({ type: e.target.value })}
          className="h-8 text-xs px-2 bg-mist-off border border-mist-soft rounded-md"
        >
          {SECTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          data-testid={`section-${index}-theme`}
          value={section.theme || "white"}
          onChange={(e) => set({ theme: e.target.value })}
          className="h-8 text-xs px-2 bg-mist-off border border-mist-soft rounded-md"
        >
          {THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={onMoveUp} className="p-1.5 text-mist-navy/60 hover:text-mist-navy" aria-label="Move up"><ChevronUp className="h-4 w-4" /></button>
          <button type="button" onClick={onMoveDown} className="p-1.5 text-mist-navy/60 hover:text-mist-navy" aria-label="Move down"><ChevronDown className="h-4 w-4" /></button>
          <button type="button" onClick={onRemove} className="p-1.5 text-mist-navy/60 hover:text-red-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <Field label="Eyebrow" value={section.eyebrow} onChange={(v) => set({ eyebrow: v })} testid={`section-${index}-eyebrow`} />
      <Field label="Heading" value={section.heading} onChange={(v) => set({ heading: v })} testid={`section-${index}-heading`} />
      <Field label="Image URL" value={section.image} onChange={(v) => set({ image: v })} testid={`section-${index}-image`} />
      <StringList label="Paragraphs" values={section.paragraphs || []} onChange={(v) => set({ paragraphs: v })} testid={`section-${index}-paragraphs`} />
      <StringList label="List items" values={section.list_items || []} onChange={(v) => set({ list_items: v })} testid={`section-${index}-list_items`} />
      <JsonField label="Cards" value={section.cards || []} onChange={(v) => set({ cards: v })} testid={`section-${index}-cards`} />
      <JsonField label="CTAs" value={section.ctas || []} onChange={(v) => set({ ctas: v })} testid={`section-${index}-ctas`} rows={4} />
      <JsonField label="Inline links" value={section.links || []} onChange={(v) => set({ links: v })} testid={`section-${index}-links`} rows={4} />
      {section.type === "faq" && (
        <JsonField label="FAQ items" value={section.faqs || []} onChange={(v) => set({ faqs: v })} testid={`section-${index}-faqs`} rows={8} />
      )}
    </div>
  );
}

export default function SectionsEditor({ sections = [], onChange }) {
  function updateAt(i, next) {
    const arr = [...sections];
    arr[i] = next;
    onChange(arr);
  }
  function removeAt(i) {
    onChange(sections.filter((_, j) => j !== i));
  }
  function move(i, delta) {
    const j = i + delta;
    if (j < 0 || j >= sections.length) return;
    const arr = [...sections];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  }
  function addSection() {
    onChange([...sections, { type: "rich_text", theme: "white", heading: "", paragraphs: [] }]);
  }

  return (
    <div data-testid="sections-editor">
      {sections.map((s, i) => (
        <SectionEditor
          key={i}
          index={i}
          section={s}
          onChange={(next) => updateAt(i, next)}
          onRemove={() => removeAt(i)}
          onMoveUp={() => move(i, -1)}
          onMoveDown={() => move(i, 1)}
        />
      ))}
      <button
        type="button"
        onClick={addSection}
        data-testid="add-section"
        className="w-full h-11 border border-dashed border-mist-soft rounded-md text-sm text-mist-navy hover:border-mist-gold hover:text-mist-gold flex items-center justify-center gap-2"
      >
        <Plus className="h-4 w-4" /> Add section
      </button>
    </div>
  );
}
